import {
  applicableAnswers,
  questionVisible,
} from "@/features/onboarding/intake-model/answers";
import type { IntakeAnswers } from "@/features/onboarding/intake-model/types";
import type { ClinicalIntakeSubmission } from "@/types/intakeEvidence";
import type { IntakeAnswer } from "@/types/clinician";
import { IntakeAnswerTable } from "./IntakeAnswerTable";

export function intakeSubmissionAnswers(
  submission: ClinicalIntakeSubmission,
  masterAnswers: IntakeAnswers = {},
): IntakeAnswer[] {
  const visibleAnswers = applicableAnswers(
    submission.definition.questions,
    submission.answers,
    masterAnswers,
  );
  return submission.definition.questions
    .filter((question) =>
      questionVisible(question, visibleAnswers, masterAnswers),
    )
    .map((question) => {
      const value = visibleAnswers[question.key];
      const values = Array.isArray(value) ? value : [value];
      const answer = values.length
        ? values
            .map((item) => {
              if (item === undefined || item === null || item === "")
                return "Not answered";
              if (question.type === "signature") return "Signature on file";
              if (typeof item === "boolean") return item ? "Yes" : "No";
              return (
                question.options?.find((option) => option.value === item)
                  ?.label ?? String(item)
              );
            })
            .join(", ")
        : "Not answered";
      const highlight = submission.highlights?.find((entry) =>
        entry.question_key
          ? entry.question_key === question.key
          : entry.question === question.label,
      );
      return {
        question: question.label,
        answer,
        importance: highlight?.importance ?? "context",
      };
    });
}

/** Full answers are an explicit expansion; clinical highlights stay above them. */
export function IntakeEvidenceDetails({
  submission,
  submissions,
}: {
  submission: ClinicalIntakeSubmission;
  submissions: ClinicalIntakeSubmission[];
}) {
  const master =
    submission.kind === "master"
      ? submission
      : submissions.find((item) => item.id === submission.master_submission_id);
  const answers = intakeSubmissionAnswers(submission, master?.answers);
  return (
    <details className="ph-no-capture border-t border-line-subtle first:border-0">
      <summary className="cursor-pointer p-11 type-label-sm hover:bg-sunken focus-visible:shadow-focus">
        <span className="font-medium">
          {submission.kind === "master"
            ? "Annual intake"
            : submission.definition.title}
        </span>
        <span className="ml-8 text-ink-muted">
          {new Date(submission.created_at).toLocaleDateString()} ·{" "}
          {answers.length} answers · Chart {submission.emr_sync_status}
        </span>
      </summary>
      <div className="p-11 pt-0">
        <p className="type-body-sm text-ink-muted mb-10">
          {submission.definition.title} · Version{" "}
          {submission.definition.version}
          {submission.full_completed_at
            ? ` · Annual completed ${new Date(submission.full_completed_at).toLocaleDateString()}`
            : ""}
          {submission.confirmed_at
            ? ` · Confirmed ${new Date(submission.confirmed_at).toLocaleDateString()}`
            : ""}
        </p>
        {submission.master_submission_id && !master && (
          <p role="alert" className="text-danger type-body-sm mb-10">
            The referenced annual intake revision is missing from this view.
            Resolve the evidence before review.
          </p>
        )}
        <IntakeAnswerTable answers={answers} highlightSafety />
      </div>
    </details>
  );
}
